// vlingo shared scroll-reveal.
//
// Replaces the byte-for-byte IntersectionObserver block that ~10 vlingo sections each inlined.
// A section opts in by carrying `data-vlingo-reveal` on its section root element (its inner
// `.van`/`.vbf`/etc. wrapper, not the outer `#shopify-section-…` node); the section emits that
// attribute only when its "Animate on scroll" setting is on, matching the old per-section
// `{% if section.settings.animate %}` gate. For each opted-in root we observe its `.reveal`
// descendants and add `.is-visible` once they scroll in, then unobserve.
//
// Behavior matches the old inline scripts, with one intentional consolidation:
//   - threshold 0.12, rootMargin '0px 0px -8% 0px' (article-body's old inline observer used
//     0.08/-6%; unified to the common 0.12/-8% here, so its reveal triggers marginally later)
//   - add `.is-visible`, then `io.unobserve` per element
//   - no IntersectionObserver support → reveal everything immediately (fallback)
// Reduced-motion is handled in CSS (the `.reveal` hidden state lives inside a
// `@media (prefers-reduced-motion: no-preference)` block), so reduced-motion users already see
// content regardless of this script — exactly as before.
//
// Scoping is preserved: we only ever query within each section root, never globally, and the
// `.reveal` CSS stays scoped to each `#shopify-section-{{ section.id }}` in its own <style>.

(() => {
  const REVEAL_OPTS = { threshold: 0.12, rootMargin: '0px 0px -8% 0px' };

  const init = (root) => {
    if (!root || root.dataset.vlingoRevealBound === '1') return;
    root.dataset.vlingoRevealBound = '1';

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, REVEAL_OPTS);

    els.forEach((el) => io.observe(el));
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-vlingo-reveal]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-bind sections re-rendered by the Theme Editor (Horizon dispatches shopify:section:load).
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!target) return;
    if (target.matches && target.matches('[data-vlingo-reveal]')) {
      init(target);
    } else {
      initAll(target);
    }
  });
})();
